import { useState, useMemo, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Pencil, Trash2, Upload, X, QrCode, Barcode, Eye, Search, FileText, RefreshCw, FileDown, ChevronDown, Settings } from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { QuickAddProductCategoryModal } from "@/components/products/QuickAddProductCategoryModal";
import { QuickFormFieldsEditor } from "@/components/products/QuickFormFieldsEditor";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format, startOfDay, endOfDay, subDays } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TablePagination } from "@/components/ui/table-pagination";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import JsBarcode from "jsbarcode";
import { useTenant } from "@/contexts/TenantContext";
import { ProtectedAction } from "@/components/ProtectedAction";
import { ProductFilters, ProductFilterState } from "@/components/products/ProductFilters";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import ProductDetailsView from "@/pages/product/ProductDetailsView";
import { logCrud } from "@/services/activities";

type Product = {
  id: string;
  nome: string;
  codigo_sku: string | null;
  descricao: string | null;
  preco_venda: number;
  preco_custo: number | null;
  status: string;
  foto_url: string | null;
  product_category_id: string | null;
  form_id: string | null;
  unit_measurement_purchase_id: string | null;
  unit_measurement_sale_id: string | null;
  unit_measurement_conversions: Record<string, Record<string, number>> | null;
  quantity_decimal_places: number;
};

type UnitMeasurement = {
  id: string;
  nome: string;
  slug: string;
};

export default function Products() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [purchaseUnitId, setPurchaseUnitId] = useState<string | undefined>(undefined);
  const [saleUnitId, setSaleUnitId] = useState<string | undefined>(undefined);
  const [generatedSku, setGeneratedSku] = useState<string>("");
  const skuInputRef = useRef<HTMLInputElement | null>(null);
  const [precoVenda, setPrecoVenda] = useState<number>(0);
  const [precoCusto, setPrecoCusto] = useState<number | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [quantityDecimalPlaces, setQuantityDecimalPlaces] = useState<number>(0);
  const [isQuickFormEditorOpen, setIsQuickFormEditorOpen] = useState(false);
  const [confirmCloseProduct, setConfirmCloseProduct] = useState(false);
  const [confirmCloseFormEditor, setConfirmCloseFormEditor] = useState(false);
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingBarcodePdf, setIsGeneratingBarcodePdf] = useState(false);
  const [isBarcodeDialogOpen, setIsBarcodeDialogOpen] = useState(false);
  const [barcodeType, setBarcodeType] = useState<"CODE128" | "CODE39" | "EAN13">("CODE128");
  const [includeProductCode, setIncludeProductCode] = useState(true);
  const [includeProductPrice, setIncludeProductPrice] = useState(true);
  const [productProfile, setProductProfile] = useState<{ id: string; name: string } | null>(null);
  const [productCategoryProfile, setProductCategoryProfile] = useState<{ id: string; name: string } | null>(null);
  const [rxFrom, setRxFrom] = useState<Date | null>(null);
  const [rxTo, setRxTo] = useState<Date | null>(null);
  const [filters, setFilters] = useState<ProductFilterState>({
    searchTerm: "",
    status: [],
    priceRange: "",
  });

  const { data: products, isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select(`
          *,
          product_categories (
            id,
            nome,
            cor_hex
          )
        `)
        .order("nome", { ascending: true });
      
      if (error) throw error;
      return (data || []) as unknown as Product[];
    },
  });

  // Query para carregar unidades de medida
  const { data: unitMeasurements } = useQuery({
    queryKey: ["unit_measurements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("unit_measurements" as any)
        .select("*")
        .order("nome", { ascending: true });
      
      if (error) throw error;
      return (data || []) as unknown as UnitMeasurement[];
    },
  });

  // Query para carregar categorias de produto
  const { data: productCategories = [] } = useQuery({
    queryKey: ["product_categories", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("product_categories")
        .select("id, nome")
        .eq("tenant_id", tenantId)
        .eq("status", "ativo")
        .order("nome");
      if (error) throw error;
      return data || [];
    },
  });

  // Query para carregar formulários
  const { data: forms = [] } = useQuery({
    queryKey: ["forms-for-products", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("forms")
        .select("id, nome")
        .eq("tenant_id", tenantId)
        .eq("status", "ativo")
        .order("nome");
      if (error) {
        console.error("Erro ao buscar formulários:", error);
        throw error;
      }
      return data || [];
    },
  });

  // Helper para obter o ID da unidade padrão pelo slug 'und'
  const getUndUnitId = () => unitMeasurements?.find((u) => u.slug === "und")?.id;

  // Função para gerar SKU automaticamente
  const generateSku = (productName: string): string => {
    if (!productName || productName.trim().length === 0) {
      return "";
    }

    // Extrair iniciais das palavras (máximo 3 palavras, máximo 3 letras por palavra)
    const words = productName
      .trim()
      .split(/\s+/)
      .filter(word => word.length > 0)
      .slice(0, 3); // Pega no máximo 3 palavras

    const initials = words
      .map(word => {
        // Remove acentos e caracteres especiais, pega a primeira letra
        const normalized = word.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const firstLetter = normalized.charAt(0).toUpperCase();
        return /[A-Z]/.test(firstLetter) ? firstLetter : "";
      })
      .filter(letter => letter.length > 0)
      .join("")
      .toUpperCase()
      .slice(0, 6); // Máximo 6 letras

    // Se não conseguiu gerar iniciais, usa as primeiras 3 letras do nome
    let prefix = initials;
    if (prefix.length === 0) {
      const normalized = productName.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      prefix = normalized
        .replace(/[^A-Za-z]/g, "")
        .toUpperCase()
        .slice(0, 3);
    }

    // Gerar número aleatório de 3 dígitos
    const randomNumber = Math.floor(100 + Math.random() * 900); // Entre 100 e 999

    return `${prefix}-${randomNumber}`;
  };

  // Quando as unidades carregarem e for um novo produto, definir padrão 'und' se ainda não selecionado
  useEffect(() => {
    if (!editingProduct) {
      const undId = getUndUnitId();
      if (undId && (!purchaseUnitId || !saleUnitId)) {
        setPurchaseUnitId((prev) => prev || undId);
        setSaleUnitId((prev) => prev || undId);
      }
    }
  }, [unitMeasurements]);

  // Atualizar o campo SKU quando generatedSku mudar (apenas para novos produtos)
  useEffect(() => {
    if (!editingProduct && generatedSku && skuInputRef.current) {
      skuInputRef.current.value = generatedSku;
    }
  }, [generatedSku, editingProduct]);

  const uploadPhoto = async (file: File, productId: string) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${productId}-${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('product-photos')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('product-photos')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleGenerateBarcodePdf = async () => {
    if (!products || products.length === 0) {
      toast.error("Nenhum produto disponível para gerar o PDF.");
      return;
    }

    setIsGeneratingBarcodePdf(true);
    try {
      const jsPdfModule = await import("jspdf");
      const jsPDFConstructor = (jsPdfModule as any).jsPDF ?? (jsPdfModule as any).default;

      if (!jsPDFConstructor) {
        throw new Error("jsPDF não pôde ser carregado");
      }

      const doc = new jsPDFConstructor({ unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const marginX = 15;
      const marginY = 20;
      const columns = 3;
      const rows = 6;
      const gapX = 6;
      const gapY = 6;
      const cardWidth = (pageWidth - marginX * 2 - gapX * (columns - 1)) / columns;
      const cardHeight = (pageHeight - marginY * 2 - gapY * (rows - 1)) / rows;
      const barcodeWidth = cardWidth * 0.8;
      const cardPaddingY = 5;
      const nameToBarcodeSpacing = 3;
      const infoLineHeight = 3;
      const minBarcodeHeight = cardHeight * 0.25;
      const infoLineCount = Number(includeProductCode) + Number(includeProductPrice);
      const infoBlockSpacing = infoLineHeight * Math.max(infoLineCount - 1, 0);
      const hasInfo = infoLineCount > 0;
      const barcodeToInfoSpacing = hasInfo ? 3 : cardPaddingY;

      const drawPageHeader = () => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        doc.text("Produtos - Códigos de Barras", pageWidth / 2, marginY - 8, { align: "center" });
      };

      drawPageHeader();

      const formatProductName = (name: string) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        const maxWidth = cardWidth - 12;
        if (doc.getTextWidth(name) <= maxWidth) {
          return name;
        }

        const ellipsis = "...";
        let truncated = name.trim();

        while (truncated.length > 0 && doc.getTextWidth(`${truncated}${ellipsis}`) > maxWidth) {
          truncated = truncated.slice(0, -1).trimEnd();
        }

        return truncated.length ? `${truncated}${ellipsis}` : ellipsis;
      };

      const getBarcodeValue = (product: Product, index: number) => {
        const baseValue = product.codigo_sku || product.id || String(index + 1);

        if (barcodeType === "EAN13") {
          let digits = baseValue.replace(/\D/g, "");
          if (!digits) {
            digits = String(index + 1);
          }
          if (digits.length < 12) {
            digits = digits.padStart(12, "0");
          }
          if (digits.length > 12) {
            digits = digits.slice(-12);
          }
          return digits;
        }

        return baseValue;
      };

      for (let index = 0; index < products.length; index++) {
        const product = products[index];
        const columnIndex = index % columns;
        const rowIndex = Math.floor((index % (columns * rows)) / columns);

        if (index > 0 && index % (columns * rows) === 0) {
          doc.addPage();
          drawPageHeader();
        }

        const posX = marginX + columnIndex * (cardWidth + gapX);
        const posY = marginY + rowIndex * (cardHeight + gapY);
        const nameY = posY + cardPaddingY;
        const barcodeX = posX + (cardWidth - barcodeWidth) / 2;

        const availableBarcodeHeight = cardHeight - cardPaddingY - nameToBarcodeSpacing - barcodeToInfoSpacing - infoBlockSpacing - cardPaddingY;
        const barcodeHeight = Math.max(availableBarcodeHeight, minBarcodeHeight);
        const barcodeY = nameY + nameToBarcodeSpacing;

        doc.setDrawColor(220);
        doc.setLineWidth(0.2);
        doc.roundedRect(posX, posY, cardWidth, cardHeight, 2, 2, "S");

        const displayName = formatProductName(product.nome);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(displayName, posX + cardWidth / 2, nameY, { align: "center" });

        const barcodeValue = getBarcodeValue(product, index);
        const pxPerMm = 96 / 25.4;
        const deviceScale = Math.max(2, Math.ceil(window.devicePixelRatio || 1));
        const canvasWidth = Math.max(Math.round(barcodeWidth * pxPerMm * deviceScale), deviceScale * 10);
        const canvasHeight = Math.max(Math.round(barcodeHeight * pxPerMm * deviceScale), deviceScale * 16);
        const canvas = document.createElement("canvas");
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        const context = canvas.getContext("2d");
        if (context) {
          context.fillStyle = "#ffffff";
          context.fillRect(0, 0, canvasWidth, canvasHeight);
          context.imageSmoothingEnabled = false;
        }

        const estimatedBars = Math.max(barcodeValue.length * 11, 60);
        const moduleWidth = Math.max(Math.floor(canvasWidth / estimatedBars), deviceScale);
        const moduleHeight = Math.max(canvasHeight - deviceScale * 8, deviceScale * 24);

        try {
          JsBarcode(canvas, barcodeValue, {
            format: barcodeType,
            displayValue: false,
            margin: 0,
            marginBottom: deviceScale * 4,
            width: moduleWidth,
            height: moduleHeight,
            background: "#ffffff",
          });
        } catch (error) {
          console.error("Erro ao gerar código de barras", { product, error });
          toast.error(`Não foi possível gerar o código de barras para ${product.nome}`);
          continue;
        }

        const barcodeDataUrl = canvas.toDataURL("image/png");
        const barcodeRenderedHeight = barcodeHeight;
        doc.addImage(barcodeDataUrl, "PNG", barcodeX, barcodeY, barcodeWidth, barcodeRenderedHeight);

        let infoYOffset = hasInfo ? posY + cardHeight - cardPaddingY - infoBlockSpacing : 0;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);

        if (includeProductCode) {
          const codigo = product.codigo_sku || product.id;
          doc.text(`Código: ${codigo}`, posX + cardWidth / 2, infoYOffset, { align: "center" });
          infoYOffset += infoLineHeight;
        }

        if (includeProductPrice) {
          doc.setFont("helvetica", "bold");
          doc.text(`Preço: ${formatCurrency(product.preco_venda)}`, posX + cardWidth / 2, infoYOffset, { align: "center" });
          infoYOffset += infoLineHeight;
        }
      }

      const pdfUrl = doc.output("bloburl");
      const pdfWindow = window.open(pdfUrl, "_blank");

      if (!pdfWindow) {
        doc.save("produtos-codigos-barras.pdf");
      }

      toast.success("PDF de códigos de barras gerado com sucesso.");
      setIsBarcodeDialogOpen(false);
    } catch (error) {
      console.error("Erro ao gerar PDF de códigos de barras", error);
      toast.error("Erro ao gerar PDF de códigos de barras.");
    } finally {
      setIsGeneratingBarcodePdf(false);
    }
  };

  const deletePhoto = async (url: string) => {
    if (!url) return;
    const path = url.split('/product-photos/')[1];
    if (path) {
      await supabase.storage.from('product-photos').remove([path]);
    }
  };

  const createMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      if (!tenantId) throw new Error("Tenant ID não encontrado");
      
      const productData = {
        nome: formData.get("nome") as string,
        codigo_sku: (formData.get("codigo_sku") as string) || generatedSku || null,
        descricao: (formData.get("descricao") as string) || null,
        preco_venda: precoVenda,
        preco_custo: precoCusto ?? null,
        status: (formData.get("status") as string) || "ativo",
        foto_url: null as string | null,
        product_category_id: selectedCategoryId || null,
        form_id: selectedFormId || null,
        unit_measurement_purchase_id: purchaseUnitId || null,
        unit_measurement_sale_id: saleUnitId || null,
        unit_measurement_conversions: {},
        quantity_decimal_places: quantityDecimalPlaces,
        tenant_id: tenantId,
      };

      const { data, error } = await supabase
        .from("products")
        .insert(productData)
        .select()
        .single();

      if (error) throw error;

      if (photoFile && data) {
        const photoUrl = await uploadPhoto(photoFile, data.id);
        const { error: updateError } = await supabase
          .from("products")
          .update({ foto_url: photoUrl })
          .eq("id", data.id);
        
        if (updateError) throw updateError;
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Produto criado com sucesso");
      if (data?.id) {
        void logCrud({ entitySlug: "entity:products", operationSlug: "create", entityId: data.id, entityName: data.nome, screenSlug: "produtos" });
      }
      setOpen(false);
      setPhotoFile(null);
      setPhotoPreview(null);
      setSelectedCategoryId(null);
      setSelectedFormId(null);
      setQuantityDecimalPlaces(0);
    },
    onError: () => toast.error("Erro ao criar produto"),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: FormData }) => {
      let photoUrl = editingProduct?.foto_url;

      if (photoFile) {
        if (editingProduct?.foto_url) {
          await deletePhoto(editingProduct.foto_url);
        }
        photoUrl = await uploadPhoto(photoFile, id);
      }

      const { error } = await supabase
        .from("products")
        .update({
          nome: formData.get("nome") as string,
          codigo_sku: (formData.get("codigo_sku") as string) || null,
          descricao: (formData.get("descricao") as string) || null,
          preco_venda: precoVenda,
          preco_custo: precoCusto ?? null,
          status: (formData.get("status") as string) || "ativo",
          foto_url: photoUrl,
          product_category_id: selectedCategoryId || null,
          form_id: selectedFormId || null,
          unit_measurement_purchase_id: purchaseUnitId || null,
          unit_measurement_sale_id: saleUnitId || null,
          quantity_decimal_places: quantityDecimalPlaces,
        })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Produto atualizado com sucesso");
      if (editingProduct) {
        void logCrud({ entitySlug: "entity:products", operationSlug: "update", entityId: editingProduct.id, entityName: editingProduct.nome, screenSlug: "produtos", before: (editingProduct as any), after: (variables as any) });
      }
      setOpen(false);
      setEditingProduct(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      setSelectedCategoryId(null);
      setSelectedFormId(null);
      setQuantityDecimalPlaces(0);
    },
    onError: () => toast.error("Erro ao atualizar produto"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const product = products?.find((p) => p.id === id);
      // Primeiro tenta excluir no banco
      const { error } = await supabase.from("products").delete().eq("id", id);
      if (error) throw error;
      // Após sucesso no banco, remove a foto (se houver)
      if (product?.foto_url) {
        await deletePhoto(product.foto_url);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Produto excluído com sucesso");
      if (deleteId) {
        void logCrud({ entitySlug: "entity:products", operationSlug: "delete", entityId: deleteId, screenSlug: "produtos" });
      }
      setDeleteId(null);
    },
    onError: (error: any) => {
      const isFK = error?.code === "23503" || error?.message?.includes("foreign key constraint") || error?.status === 409;
      if (isFK) {
        toast.error(
          "Não é possível excluir este produto porque existem registros relacionados (ex.: itens de transação). Remova ou ajuste os vínculos antes de tentar novamente."
        );
      } else {
        toast.error("Erro ao excluir produto");
      }
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, newStatus }: { id: string; newStatus: string }) => {
      const { error } = await supabase
        .from("products")
        .update({ status: newStatus })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Status alterado com sucesso");
      if (editingProduct) {
        void logCrud({ entitySlug: "entity:products", operationSlug: "update", entityId: editingProduct.id, entityName: editingProduct.nome, screenSlug: "produtos", before: (editingProduct as any), after: { status: (editingProduct as any)?.status === 'ativo' ? 'inativo' : 'ativo' } });
      }
    },
    onError: () => toast.error("Erro ao alterar status"),
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    if (editingProduct) {
      updateMutation.mutate({ id: editingProduct.id, formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handleOpenDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setPhotoPreview(product.foto_url);
      setPurchaseUnitId(product.unit_measurement_purchase_id || undefined);
      setSaleUnitId(product.unit_measurement_sale_id || undefined);
      setGeneratedSku(""); // Limpar SKU gerado ao editar
      // Inicializar categoria se existir
      setSelectedCategoryId(product.product_category_id || null);
      setSelectedFormId(product.form_id || null);
      setQuantityDecimalPlaces(product.quantity_decimal_places ?? 0);
      // Inicializar preços do produto
      setPrecoVenda(product.preco_venda || 0);
      setPrecoCusto(product.preco_custo || null);
    } else {
      setEditingProduct(null);
      setPhotoPreview(null);
      setGeneratedSku(""); // Limpar SKU gerado ao abrir novo produto
      setSelectedCategoryId(null); // Limpar categoria ao criar novo produto
      setSelectedFormId(null); // Limpar formulário ao criar novo produto
      setQuantityDecimalPlaces(0); // Valor padrão
      // Limpar preços para novo produto
      setPrecoVenda(0);
      setPrecoCusto(null);
      // Para novo produto, aplicar unidade padrão 'und' quando disponível
      const undId = getUndUnitId();
      setPurchaseUnitId(undId || undefined);
      setSaleUnitId(undId || undefined);
    }
    setPhotoFile(null);
    setOpen(true);
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  // Helpers para montar a URL pública do produto usando a BASE_URL do Vite (se configurada)
  const getBaseUrl = () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const base = import.meta.env.BASE_URL || "/";
    const normalizedBase = base === "/" ? "" : base.endsWith("/") ? base.slice(0, -1) : base;
    return `${origin}${normalizedBase}`;
  };

  const getPublicProductUrl = (id: string) => `${getBaseUrl()}/produto/${encodeURIComponent(id)}`;

  const handleGenerateQrPdf = async () => {
    if (!products || products.length === 0) {
      toast.error("Nenhum produto disponível para gerar o PDF.");
      return;
    }

    setIsGeneratingPdf(true);
    try {
      const [jsPdfModule, qrCodeModule] = await Promise.all([
        import("jspdf"),
        import("qrcode"),
      ]);

      const jsPDFConstructor = (jsPdfModule as any).jsPDF ?? (jsPdfModule as any).default;
      const QRCodeLib = (qrCodeModule as any).default ?? qrCodeModule;

      if (!jsPDFConstructor) {
        throw new Error("jsPDF não pôde ser carregado");
      }

      if (!QRCodeLib?.toDataURL) {
        throw new Error("Função toDataURL do QRCode não disponível");
      }

      const doc = new jsPDFConstructor({ unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const marginX = 15;
      const marginY = 20;
      const columns = 3;
      const rows = 4;
      const gapX = 5;
      const gapY = 5;
      const cardWidth = (pageWidth - marginX * 2 - gapX * (columns - 1)) / columns;
      const cardHeight = (pageHeight - marginY * 2 - gapY * (rows - 1)) / rows;
      const cardPaddingTop = 6;
      const headerSpacing = 6;
      const cardPaddingBottom = 4;
      const textLineSpacing = 4;
      const infoSpacing = 8;
      const qrHorizontalMargin = 6;
      const minQrSize = 24;

      const drawPageHeader = () => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        doc.text("Produtos - QR Codes", pageWidth / 2, marginY - 8, { align: "center" });
      };

      drawPageHeader();

      const formatProductName = (name: string) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        const maxWidth = cardWidth - 12;
        if (doc.getTextWidth(name) <= maxWidth) {
          return name;
        }

        const ellipsis = "...";
        let truncated = name.trim();

        while (truncated.length > 0 && doc.getTextWidth(`${truncated}${ellipsis}`) > maxWidth) {
          truncated = truncated.slice(0, -1).trimEnd();
        }

        return truncated.length ? `${truncated}${ellipsis}` : ellipsis;
      };

      for (let index = 0; index < products.length; index++) {
        const product = products[index];
        const columnIndex = index % columns;
        const rowIndex = Math.floor((index % (columns * rows)) / columns);

        if (index > 0 && index % (columns * rows) === 0) {
          doc.addPage();
          drawPageHeader();
        }

        const posX = marginX + columnIndex * (cardWidth + gapX);
        const posY = marginY + rowIndex * (cardHeight + gapY);
        const nameY = posY + cardPaddingTop;
        const qrY = nameY + headerSpacing;
        const priceLineY = posY + cardHeight - cardPaddingBottom;
        const codeLineY = priceLineY - textLineSpacing;
        let qrSize = Math.min(
          cardWidth - qrHorizontalMargin * 2,
          codeLineY - infoSpacing - qrY
        );
        if (qrSize < minQrSize) {
          qrSize = minQrSize;
        }
        const qrX = posX + (cardWidth - qrSize) / 2;

        doc.setDrawColor(220);
        doc.setLineWidth(0.2);
        doc.roundedRect(posX, posY, cardWidth, cardHeight, 2, 2, "S");

        const displayName = formatProductName(product.nome);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(displayName, posX + cardWidth / 2, nameY, { align: "center" });

        const publicUrl = getPublicProductUrl(product.id);

        const qrDataUrl = await QRCodeLib.toDataURL(publicUrl, {
          width: 300,
          margin: 1,
        });

        doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        const codigo = product.codigo_sku || product.id;
        doc.text(`Código: ${codigo}`, posX + cardWidth / 2, codeLineY, {
          align: "center",
        });
        doc.setFont("helvetica", "bold");
        doc.text(`Preço: ${formatCurrency(product.preco_venda)}`, posX + cardWidth / 2, priceLineY, {
          align: "center",
        });
      }

      const pdfUrl = doc.output("bloburl");
      const pdfWindow = window.open(pdfUrl, "_blank");

      if (!pdfWindow) {
        doc.save("produtos-qr.pdf");
      }
      toast.success("PDF gerado com sucesso.");
    } catch (error) {
      console.error("Erro ao gerar PDF de produtos", error);
      toast.error("Erro ao gerar PDF de produtos.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Abrir modal via query string e limpar parâmetro
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("newProduct") === "1") {
      handleOpenDialog();
      params.delete("newProduct");
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.search]);

  const matchesFilters = (product: Product) => {
    // Filtro por busca
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const matchesSearch =
        product.nome?.toLowerCase().includes(searchLower) ||
        product.codigo_sku?.toLowerCase().includes(searchLower) ||
        product.descricao?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Filtro por status
    if (filters.status.length > 0) {
      if (!filters.status.includes(product.status)) return false;
    }

    // Filtro por faixa de preço
    if (filters.priceRange) {
      const price = product.preco_venda;
      const [min, max] = filters.priceRange.includes("+")
        ? [5000, Infinity]
        : filters.priceRange.split("-").map(Number);
      if (price < min || price > max) return false;
    }

    return true;
  };

  const filteredProducts = useMemo(() => {
    if (!products) return [] as Product[];
    return products
      .filter((product) => matchesFilters(product))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" }));
  }, [products, filters]);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(start, start + itemsPerPage);
  }, [filteredProducts, currentPage, itemsPerPage]);

  // Calcular contagens para os filtros (ignorando o filtro específico sendo contado)
  const facetCounts = useMemo(() => {
    const counts = {
      status: {} as Record<string, number>,
      priceRanges: {} as Record<string, number>,
    };

    if (!products) return counts;

    products.forEach((product) => {
      // Verificar se o produto corresponde aos outros filtros (exceto o que está sendo contado)
      const matchesOtherFilters = (exceptFilter?: "status" | "priceRange") => {
        // Filtro por busca
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          const matchesSearch =
            product.nome?.toLowerCase().includes(searchLower) ||
            product.codigo_sku?.toLowerCase().includes(searchLower) ||
            product.descricao?.toLowerCase().includes(searchLower);
          if (!matchesSearch) return false;
        }

        // Filtro por status (ignorar se estiver contando status)
        if (exceptFilter !== "status" && filters.status.length > 0) {
          if (!filters.status.includes(product.status)) return false;
        }

        // Filtro por faixa de preço (ignorar se estiver contando preço)
        if (exceptFilter !== "priceRange" && filters.priceRange) {
          const price = product.preco_venda;
          const [min, max] = filters.priceRange.includes("+")
            ? [5000, Infinity]
            : filters.priceRange.split("-").map(Number);
          if (price < min || price > max) return false;
        }

        return true;
      };

      // Contar status (ignorando filtro de status)
      if (product.status && matchesOtherFilters("status")) {
        counts.status[product.status] = (counts.status[product.status] || 0) + 1;
      }

      // Contar faixas de preço (ignorando filtro de preço)
      if (matchesOtherFilters("priceRange")) {
        const price = product.preco_venda;
        const bucket =
          price <= 100 ? "0-100" :
          price <= 500 ? "100-500" :
          price <= 1000 ? "500-1000" :
          price <= 5000 ? "1000-5000" :
          "5000+";
        counts.priceRanges[bucket] = (counts.priceRanges[bucket] || 0) + 1;
      }
    });

    return counts;
  }, [products, filters]);

  const allStatuses = useMemo(() => {
    const statuses = new Set<string>();
    products?.forEach((p) => {
      if (p.status) statuses.add(p.status);
    });
    return Array.from(statuses).sort();
  }, [products]);

  const handleFiltersChange = (newFilters: ProductFilterState) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const activeFilterChips = useMemo<{ id: string; label: React.ReactNode; onClear: () => void }[]>(() => {
    const chips: { id: string; label: React.ReactNode; onClear: () => void }[] = [];
    
    if (filters.searchTerm) {
      const term = filters.searchTerm;
      chips.push({
        id: `search:${term}`,
        label: `Busca: "${term}"`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, searchTerm: "" });
        },
      });
    }
    
    filters.status.forEach((s) => {
      chips.push({
        id: `status:${s}`,
        label: `Status: ${s}`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, status: filters.status.filter((x) => x !== s) });
        },
      });
    });
    
    if (filters.priceRange) {
      const pr = filters.priceRange;
      let labelRange = "";
      if (pr === "0-100") {
        labelRange = "Até R$ 100";
      } else if (pr === "100-500") {
        labelRange = "R$ 100 - R$ 500";
      } else if (pr === "500-1000") {
        labelRange = "R$ 500 - R$ 1.000";
      } else if (pr === "1000-5000") {
        labelRange = "R$ 1.000 - R$ 5.000";
      } else if (pr === "5000+") {
        labelRange = "Acima de R$ 5.000";
      }
      
      chips.push({
        id: `price:${pr}`,
        label: `Preço: ${labelRange}`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, priceRange: "" });
        },
      });
    }
    
    return chips;
  }, [filters]);

  return (
    <div className="space-y-6" data-cy="products-page">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold" data-cy="products-title">Produtos</h1>
          <p className="text-muted-foreground">Gerencie seu catálogo de produtos</p>
        </div>
        <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center md:justify-end md:gap-3">
          <div className="flex w-full flex-row flex-wrap gap-2 md:w-auto">
            {(globalThis as any).__TEST_INLINE_MENU__ ? (
              <div className="flex w-full flex-row flex-wrap gap-2 md:w-auto">
                <Button
                  className="flex-1 min-w-[150px] md:w-auto"
                  variant="outline"
                  disabled={isGeneratingPdf || !products?.length}
                  data-cy="products-qrcode-pdf-btn"
                  onClick={handleGenerateQrPdf}
                >
                  <QrCode className="mr-2 h-4 w-4" />
                  PDF de QR Codes
                </Button>
                <Button
                  className="flex-1 min-w-[150px] md:w-auto"
                  variant="outline"
                  disabled={isGeneratingBarcodePdf || !products?.length}
                  data-cy="products-barcode-pdf-open-btn"
                  onClick={() => setIsBarcodeDialogOpen(true)}
                >
                  <Barcode className="mr-2 h-4 w-4" />
                  PDF de Cód. de Barras
                </Button>
              </div>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="flex-1 min-w-[150px] md:w-auto"
                    variant="outline"
                    disabled={isGeneratingPdf || isGeneratingBarcodePdf || !products?.length}
                    data-cy="products-generate-pdf-btn"
                  >
                    <FileDown className="mr-2 h-4 w-4" />
                    Gerar PDF
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" forceMount>
                  <DropdownMenuItem
                    onClick={handleGenerateQrPdf}
                    disabled={isGeneratingPdf || !products?.length}
                    data-cy="products-qrcode-pdf-btn"
                  >
                    <QrCode className="mr-2 h-4 w-4" />
                    PDF de QR Codes
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setIsBarcodeDialogOpen(true)}
                    disabled={isGeneratingBarcodePdf || !products?.length}
                    data-cy="products-barcode-pdf-open-btn"
                  >
                    <Barcode className="mr-2 h-4 w-4" />
                    PDF de Cód. de Barras
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Dialog
              open={open}
              onOpenChange={(next) => {
                if (!next) {
                  setConfirmCloseProduct(true);
                } else {
                  setOpen(true);
                }
              }}
            >
              <ProtectedAction module="products" action="create">
                <DialogTrigger asChild>
                  <Button className="flex-1 min-w-[150px] md:w-auto" onClick={() => handleOpenDialog()} data-cy="new-product-btn">
                    <Plus className="mr-2 h-4 w-4" />
                    Novo Produto
                  </Button>
                </DialogTrigger>
              </ProtectedAction>
              <DialogContent className="max-w-4xl">
                <DialogHeader>
                  <DialogTitle>{editingProduct ? "Editar Produto" : "Novo Produto"}</DialogTitle>
                  <DialogDescription>
                    Preencha os dados do produto
                  </DialogDescription>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <form id="product-form" onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-start gap-4">
                        {/* Esquerda: coluna fixa com label e imagem */}
                        <div className="w-32">
                          <Label htmlFor="foto">Foto do Produto</Label>
                          {photoPreview ? (
                            <div
                              className="relative cursor-pointer mt-2"
                              onClick={() => fileInputRef.current?.click()}
                              title="Selecionar outra imagem"
                            >
                              <img
                                src={photoPreview}
                                alt="Preview"
                                className="w-32 h-32 object-cover rounded-lg border"
                              />
                              <Button
                                type="button"
                                variant="destructive"
                                size="icon"
                                className="absolute -top-2 -right-2 h-6 w-6"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemovePhoto();
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div
                              className="mt-2 w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted/50"
                              onClick={() => fileInputRef.current?.click()}
                              title="Selecionar imagem"
                            >
                              <Upload className="h-8 w-8 text-muted-foreground" />
                            </div>
                          )}
                          <input
                            ref={fileInputRef}
                            id="foto"
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoChange}
                            className="hidden"
                            aria-hidden="true"
                          />
                        </div>

                        {/* Direita: Nome e Preços */}
                        <div className="flex-1 space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="nome">Nome *</Label>
                              <Input
                                id="nome"
                                name="nome"
                                defaultValue={editingProduct?.nome || ""}
                                required
                                data-cy="product-name-input"
                                onChange={(e) => {
                                  // Gerar SKU automaticamente apenas se estiver criando um novo produto
                                  if (!editingProduct && e.target.value.trim().length > 0) {
                                    const newSku = generateSku(e.target.value);
                                    // Só atualiza se o SKU atual foi gerado automaticamente ou está vazio
                                    if (newSku && (!generatedSku || skuInputRef.current?.value === generatedSku || !skuInputRef.current?.value)) {
                                      setGeneratedSku(newSku);
                                    }
                                  } else if (!editingProduct && e.target.value.trim().length === 0) {
                                    // Limpar SKU se o nome for apagado
                                    setGeneratedSku("");
                                  }
                                }}
                              />
                            </div>
                            <div className="space-y-2">
                              {selectedCategoryId ? (
                                <TooltipProvider delayDuration={300}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setProductCategoryProfile({ id: selectedCategoryId, name: (productCategories || []).find((cat: any) => cat.id === selectedCategoryId)?.nome || 'categoria' });
                                        }}
                                        className="flex items-center gap-1 hover:text-blue-600 transition-colors group"
                                      >
                                        <Label className="cursor-pointer group-hover:text-blue-600 mt-1 mb-1">Categoria</Label>
                                        <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent className="whitespace-pre-line z-[9999]" side="bottom" sideOffset={8} align="start">
                                      Ver ficha completa e relatórios{'\n'}da categoria <strong>{(productCategories || []).find((cat: any) => cat.id === selectedCategoryId)?.nome || 'categoria'}</strong>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <Label>Categoria</Label>
                              )}
                              <SearchableSelect
                                value={selectedCategoryId || ""}
                                onChange={(value) => setSelectedCategoryId(value || null)}
                                options={(productCategories || []).map((cat: any) => ({ value: cat.id, label: cat.nome }))}
                                placeholder="Selecione a categoria"
                                renderAddModal={({ open, onOpenChange, onCreated }) => (
                                  <QuickAddProductCategoryModal
                                    open={open}
                                    onOpenChange={onOpenChange}
                                    onCategoryCreated={(category) => {
                                      setSelectedCategoryId(category.id);
                                      onCreated(category.nome);
                                      queryClient.invalidateQueries({ queryKey: ["product_categories", tenantId] });
                                    }}
                                  />
                                )}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="preco_venda">Preço de Venda *</Label>
                              <MoneyInput
                                id="preco_venda"
                                name="preco_venda"
                                value={precoVenda}
                                onValueChange={(value) => {
                                  setPrecoVenda(value);
                                  // Atualizar input hidden para FormData
                                  const hiddenInput = document.getElementById("preco_venda_hidden") as HTMLInputElement;
                                  if (hiddenInput) {
                                    hiddenInput.value = value.toString();
                                  }
                                }}
                                placeholder="0,00"
                                required
                                data-cy="product-price-input"
                              />
                              <input
                                type="hidden"
                                id="preco_venda_hidden"
                                name="preco_venda"
                                value={precoVenda}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="preco_custo">Preço de Custo</Label>
                              <MoneyInput
                                id="preco_custo"
                                value={precoCusto ?? 0}
                                onValueChange={(value) => {
                                  setPrecoCusto(value > 0 ? value : null);
                                  // Atualizar input hidden para FormData
                                  const hiddenInput = document.getElementById("preco_custo_hidden") as HTMLInputElement;
                                  if (hiddenInput) {
                                    hiddenInput.value = value > 0 ? value.toString() : "";
                                  }
                                }}
                                placeholder="0,00"
                                data-cy="product-cost-input"
                              />
                              <input
                                type="hidden"
                                id="preco_custo_hidden"
                                name="preco_custo"
                                value={precoCusto ?? ""}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Campos de Unidade de Medida */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="unit_measurement_purchase_id">Unidade de Compra</Label>
                        <Select 
                          value={purchaseUnitId} 
                          onValueChange={setPurchaseUnitId}
                        >
                          <SelectTrigger data-cy="product-purchase-unit-select">
                            <SelectValue placeholder="Nenhuma" />
                          </SelectTrigger>
                          <SelectContent>
                            {unitMeasurements?.map((unit) => (
                              <SelectItem key={unit.id} value={unit.id}>
                                {unit.nome} ({unit.slug})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="unit_measurement_sale_id">Unidade de Venda</Label>
                        <Select 
                          value={saleUnitId} 
                          onValueChange={setSaleUnitId}
                        >
                          <SelectTrigger data-cy="product-sale-unit-select">
                            <SelectValue placeholder="Nenhuma" />
                          </SelectTrigger>
                          <SelectContent>
                            {unitMeasurements?.map((unit) => (
                              <SelectItem key={unit.id} value={unit.id}>
                                {unit.nome} ({unit.slug})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="quantity_decimal_places">Casas Decimais p/ Qtd</Label>
                        <Input
                          id="quantity_decimal_places"
                          name="quantity_decimal_places"
                          type="number"
                          min="0"
                          max="10"
                          step="1"
                          value={quantityDecimalPlaces}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            // Permitir apenas números inteiros
                            if (inputValue === "" || /^\d+$/.test(inputValue)) {
                              const value = inputValue === "" ? 0 : parseInt(inputValue, 10);
                              if (value >= 0 && value <= 10) {
                                setQuantityDecimalPlaces(value);
                              }
                            }
                          }}
                          onKeyDown={(e) => {
                            // Bloquear teclas que não são números ou teclas de controle
                            const key = e.key;
                            const isNumber = /^[0-9]$/.test(key);
                            const isControlKey = [
                              'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                              'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                              'Home', 'End'
                            ].includes(key);
                            const isCtrlA = e.ctrlKey && key === 'a';
                            const isCtrlC = e.ctrlKey && key === 'c';
                            const isCtrlV = e.ctrlKey && key === 'v';
                            const isCtrlX = e.ctrlKey && key === 'x';
                            
                            if (!isNumber && !isControlKey && !isCtrlA && !isCtrlC && !isCtrlV && !isCtrlX) {
                              e.preventDefault();
                            }
                          }}
                          data-cy="product-quantity-decimal-places-input"
                        />
                      </div>
                    </div>

                    {/* Descrição abaixo do bloco de imagem + nome + preços */}
                    <div className="space-y-2">
                      <Label htmlFor="descricao">Descrição</Label>
                      <Textarea
                        id="descricao"
                        name="descricao"
                        defaultValue={editingProduct?.descricao || ""}
                        rows={3}
                        data-cy="product-description-input"
                      />
                    </div>

                    {/* Código/SKU e Status depois dos preços */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="codigo_sku">Código/SKU</Label>
                          {editingProduct && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => {
                                const nomeInput = document.getElementById("nome") as HTMLInputElement;
                                const nome = nomeInput?.value || editingProduct?.nome || "";
                                if (nome.trim().length > 0) {
                                  const newSku = generateSku(nome);
                                  if (newSku) {
                                    setGeneratedSku(newSku);
                                    if (skuInputRef.current) {
                                      skuInputRef.current.value = newSku;
                                      // Disparar evento change para garantir que o valor seja capturado no form
                                      skuInputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
                                    }
                                  }
                                } else {
                                  toast.error("Digite o nome do produto primeiro");
                                }
                              }}
                              title="Gerar novo SKU"
                            >
                              <RefreshCw className="h-2.5 w-2.5 mr-1" />
                              Gerar
                            </Button>
                          )}
                        </div>
                        <Input
                          ref={skuInputRef}
                          id="codigo_sku"
                          name="codigo_sku"
                          defaultValue={editingProduct ? (editingProduct?.codigo_sku || "") : (generatedSku || "")}
                          onChange={(e) => {
                            // Atualizar o estado quando o usuário editar manualmente (apenas para novos produtos)
                            if (!editingProduct && e.target.value !== generatedSku) {
                              setGeneratedSku(e.target.value);
                            }
                          }}
                          data-cy="product-sku-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="status">Status</Label>
                        <Select name="status" defaultValue={editingProduct?.status || "ativo"}>
                          <SelectTrigger data-cy="product-status-select">
                            <SelectValue placeholder="Selecione o status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ativo">Ativo</SelectItem>
                            <SelectItem value="inativo">Inativo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Campo de Formulário */}
                    <div className="space-y-2">
                      <Label htmlFor="form_id">Formulário</Label>
                        <div className="flex gap-2">
                        <div className="flex-1">
                          <Select 
                            value={selectedFormId || "__none__"} 
                            onValueChange={(value) => {
                              setSelectedFormId(value === "__none__" ? null : value);
                            }}
                          >
                            <SelectTrigger data-cy="product-form-select">
                              <SelectValue placeholder="Nenhum formulário" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">Nenhum formulário</SelectItem>
                              {forms.map((form: any) => (
                                <SelectItem key={form.id} value={form.id}>
                                  {form.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        </div>
                        {!selectedFormId && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setIsQuickFormEditorOpen(true)}
                            title="Criar novo formulário"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        )}
                        {selectedFormId && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setIsQuickFormEditorOpen(true)}
                            title="Editar campos do formulário"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
                <div className="bg-background px-6 py-4 border-t flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setConfirmCloseProduct(true)}>
                    Cancelar
                  </Button>
                  <Button type="submit" form="product-form" data-cy="product-submit-btn">
                    {editingProduct ? "Atualizar" : "Criar"} Produto
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <QuickFormFieldsEditor
              formId={selectedFormId}
              open={isQuickFormEditorOpen}
              onOpenChange={(next) => {
                if (!next) {
                  setConfirmCloseFormEditor(true);
                } else {
                  setIsQuickFormEditorOpen(true);
                }
              }}
              onFormCreated={(newFormId) => {
                setSelectedFormId(newFormId);
                queryClient.invalidateQueries({ queryKey: ["forms"] });
              }}
            />
            <Dialog open={isBarcodeDialogOpen} onOpenChange={setIsBarcodeDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Configurar códigos de barras</DialogTitle>
                  <DialogDescription>
                    Escolha o tipo e quais informações serão exibidas.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-6">
                  <div className="space-y-3">
                    <Label>Tipo de código de barras</Label>
                    <RadioGroup value={barcodeType} onValueChange={(value) => setBarcodeType(value as typeof barcodeType)}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="CODE128" id="barcode-code128" />
                        <Label htmlFor="barcode-code128" className="font-normal">CODE 128</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="CODE39" id="barcode-code39" />
                        <Label htmlFor="barcode-code39" className="font-normal">CODE 39</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="EAN13" id="barcode-ean13" />
                        <Label htmlFor="barcode-ean13" className="font-normal">EAN-13</Label>
                      </div>
                    </RadioGroup>
                    {barcodeType === "EAN13" && (
                      <p className="text-sm text-muted-foreground">
                        Para EAN-13, usaremos o SKU ou ID numérico ajustado para 12 dígitos.
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="barcode-show-code">Incluir código do produto</Label>
                      <Switch
                        id="barcode-show-code"
                        checked={includeProductCode}
                        onCheckedChange={setIncludeProductCode}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="barcode-show-price">Incluir preço</Label>
                      <Switch
                        id="barcode-show-price"
                        checked={includeProductPrice}
                        onCheckedChange={setIncludeProductPrice}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsBarcodeDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleGenerateBarcodePdf}
                      disabled={isGeneratingBarcodePdf}
                      data-cy="products-barcode-pdf-generate-btn"
                    >
                      {isGeneratingBarcodePdf ? "Gerando..." : "Gerar PDF"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <Card data-cy="products-table-card">
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle data-cy="products-table-title">Lista de Produtos</CardTitle>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative w-full sm:w-[200px]">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFiltersChange({ ...filters, searchTerm: e.target.value })}
                    className="pl-8"
                    data-cy="products-search-input"
                  />
                </div>
                <ProductFilters
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  statuses={allStatuses}
                  counts={facetCounts}
                  data-cy="products-filters"
                />
              </div>
            </div>
            {activeFilterChips.length > 0 && (
              <div className="text-[13px] md:text-sm text-muted-foreground">
                <span className="mr-2 font-bold">Filtros ativos:</span>
                <div className="inline-flex flex-wrap gap-0.5">
                  {activeFilterChips.map((chip) => (
                    <Badge key={chip.id} variant="secondary" className="h-5 px-1.5 py-0 inline-flex items-center gap-1 border border-gray-300">
                      <span>{chip.label}</span>
                      <button
                        type="button"
                        onClick={chip.onClear}
                        className="text-red-600 hover:bg-red-600 hover:text-white rounded-full w-4 h-4 inline-flex items-center justify-center leading-none"
                        aria-label={`Remover ${chip.label}`}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8" data-cy="products-loading">Carregando...</div>
          ) : filteredProducts && filteredProducts.length > 0 ? (
            <>
              <div className="space-y-3 md:hidden">
                {paginatedProducts.map((product) => (
                  <div
                    key={product.id}
                    className="rounded-lg border bg-card shadow-sm transition hover:shadow-md"
                    onClick={() => handleOpenDialog(product)}
                    data-cy={`product-card-${product.id}`}
                  >
                    <div className="flex gap-3 px-4 py-3">
                      <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border bg-muted">
                        {product.foto_url ? (
                          <img
                            src={product.foto_url}
                            alt={product.nome}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                            <Upload className="h-5 w-5" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col gap-1">
                        <h3 className="text-base font-semibold leading-tight" data-cy={`product-name-${product.id}`}>
                          {product.nome}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Badge
                                    variant={product.status === "ativo" ? "default" : "secondary"}
                                    className={`cursor-pointer text-xs ${
                                      product.status === "ativo"
                                        ? "bg-success/10 text-success hover:bg-success/20"
                                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleStatusMutation.mutate({
                                        id: product.id,
                                        newStatus: product.status === "ativo" ? "inativo" : "ativo",
                                      });
                                    }}
                                    data-cy={`product-status-badge-mobile-${product.id}`}
                                  >
                                    {product.status}
                                  </Badge>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <div className="text-center">
                                  <div>
                                    {product.status === "ativo" 
                                      ? "Clique para DESATIVAR" 
                                      : "Clique para ATIVAR"}
                                  </div>
                                  <div className="font-bold">o produto {product.nome}</div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <span data-cy={`product-sku-${product.id}`}>
                            {product.codigo_sku || "SKU não informado"}
                          </span>
                          {(() => {
                            const categoria = (product as any).product_categories;
                            if (!categoria || !categoria.nome) return null;
                            
                            return (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setProductCategoryProfile({ id: categoria.id, name: categoria.nome });
                                      }}
                                      className="inline-flex items-center gap-1 hover:opacity-80 transition-opacity"
                                    >
                                      <Badge 
                                        variant="secondary" 
                                        className="text-xs cursor-pointer"
                                        style={categoria.cor_hex ? { 
                                          backgroundColor: categoria.cor_hex,
                                          color: "#ffffff",
                                          borderColor: categoria.cor_hex
                                        } : undefined}
                                      >
                                        {categoria.nome}
                                        <FileText className="h-3 w-3 ml-1" />
                                      </Badge>
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent className="whitespace-pre-line">
                                    Ver ficha completa e relatórios{'\n'}da categoria <strong>{categoria.nome}</strong>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })()}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2" data-cy={`product-price-${product.id}`}>
                          <span className="text-sm font-semibold text-foreground">
                            {formatCurrency(product.preco_venda)}
                          </span>
                          {product.preco_custo !== null && (
                            <span className="text-xs text-muted-foreground">
                              Custo: {formatCurrency(product.preco_custo)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 border-t px-4 py-2">
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 icon-chip-blue"
                              onClick={(e) => {
                                e.stopPropagation();
                                setProductProfile({ id: product.id, name: product.nome });
                              }}
                              data-cy={`product-details-btn-mobile-${product.id}`}
                            >
                              <FileText className="mr-2 h-4 w-4" />
                              Detalhes
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            Ver ficha completa e relatórios do produto{" "}
                            <span className="font-bold">{product.nome}</span>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 icon-chip-blue"
                              onClick={(e) => {
                                e.stopPropagation();
                                const url = getPublicProductUrl(product.id);
                                window.open(url, "_blank", "noopener,noreferrer");
                              }}
                              data-cy={`product-view-btn-mobile-${product.id}`}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              Ver público
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <div className="text-center">
                              <div>Visualizar página pública</div>
                              <div className="font-bold">{product.nome}</div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <ProtectedAction module="products" action="update">
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDialog(product);
                                }}
                                data-cy={`product-edit-btn-mobile-${product.id}`}
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                Editar
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <div className="text-center">
                                <div>Editar produto</div>
                                <div className="font-bold">{product.nome}</div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </ProtectedAction>
                      <ProtectedAction module="products" action="delete">
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteId(product.id);
                                }}
                                data-cy={`product-delete-btn-mobile-${product.id}`}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Excluir
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <div className="text-center">
                                <div>Excluir produto</div>
                                <div className="font-bold">{product.nome}</div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </ProtectedAction>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden md:block">
                <Table data-cy="products-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Foto</TableHead>
                      <TableHead>Nome / SKU</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead>Preço</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedProducts.map((product) => (
                      <TableRow
                        key={product.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleOpenDialog(product)}
                        data-cy={`product-row-${product.id}`}
                      >
                        <TableCell data-cy={`product-photo-${product.id}`}>
                          {product.foto_url ? (
                            <img
                              src={product.foto_url}
                              alt={product.nome}
                              className="w-10 h-10 object-cover rounded"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-muted rounded flex items-center justify-center">
                              <Upload className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex flex-col">
                            <span>{product.nome}</span>
                            <span className="text-xs text-muted-foreground">{product.codigo_sku || "—"}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const categoria = (product as any).product_categories;
                            if (!categoria || !categoria.nome) {
                              return <span className="text-muted-foreground">—</span>;
                            }
                            
                            return (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setProductCategoryProfile({ id: categoria.id, name: categoria.nome });
                                      }}
                                      className="inline-flex items-center hover:opacity-80 transition-opacity"
                                    >
                                      <Badge 
                                        variant="secondary"
                                        className="cursor-pointer"
                                        style={categoria.cor_hex ? { 
                                          backgroundColor: categoria.cor_hex,
                                          color: "#ffffff",
                                          borderColor: categoria.cor_hex
                                        } : undefined}
                                      >
                                        {categoria.nome}
                                        <FileText className="h-4 w-4 ml-1" />
                                      </Badge>
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent className="whitespace-pre-line">
                                    Ver ficha completa e relatórios{'\n'}da categoria <strong>{categoria.nome}</strong>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="font-semibold cursor-help">{formatCurrency(product.preco_venda)}</span>
                                </TooltipTrigger>
                                <TooltipContent side="top">Preço de Venda</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            {product.preco_custo !== null && (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-xs text-muted-foreground cursor-help">{formatCurrency(product.preco_custo)}</span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">Preço de Custo</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Badge
                                    variant={product.status === "ativo" ? "default" : "secondary"}
                                    className={`cursor-pointer ${
                                      product.status === "ativo"
                                        ? "bg-success/10 text-success hover:bg-success/20"
                                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleStatusMutation.mutate({
                                        id: product.id,
                                        newStatus: product.status === "ativo" ? "inativo" : "ativo",
                                      });
                                    }}
                                    data-cy={`product-status-badge-${product.id}`}
                                  >
                                    {product.status}
                                  </Badge>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <div className="text-center">
                                  <div>
                                    {product.status === "ativo" 
                                      ? "Clique para DESATIVAR" 
                                      : "Clique para ATIVAR"}
                                  </div>
                                  <div className="font-bold">o produto {product.nome}</div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell className="text-right">
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="icon-chip-blue"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setProductProfile({ id: product.id, name: product.nome });
                                }}
                                data-cy={`product-details-btn-${product.id}`}
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <div className="text-center">
                                  <div>Ver ficha completa e relatórios do produto</div>
                                  <div className="font-bold">{product.nome}</div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="icon-chip-blue"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const url = getPublicProductUrl(product.id);
                                    window.open(url, "_blank", "noopener,noreferrer");
                                  }}
                                  data-cy={`product-view-btn-${product.id}`}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <div className="text-center">
                                  <div>Visualizar página pública</div>
                                  <div className="font-bold">{product.nome}</div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <ProtectedAction module="products" action="update">
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="icon-chip-green"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenDialog(product);
                                    }}
                                    data-cy={`product-edit-btn-${product.id}`}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  <div className="text-center">
                                    <div>Editar produto</div>
                                    <div className="font-bold">{product.nome}</div>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </ProtectedAction>
                          <ProtectedAction module="products" action="delete">
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="icon-chip-red"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDeleteId(product.id);
                                    }}
                                    data-cy={`product-delete-btn-${product.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  <div className="text-center">
                                    <div>Excluir produto</div>
                                    <div className="font-bold">{product.nome}</div>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </ProtectedAction>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <TablePagination
              currentPage={currentPage}
              totalPages={Math.ceil(filteredProducts.length / itemsPerPage)}
              totalItems={filteredProducts.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(value) => {
                setItemsPerPage(value);
                setCurrentPage(1);
              }}
              data-cy="products-pagination"
            />
            </>
          ) : products && products.length > 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-cy="products-empty-filtered">
              Nenhum produto encontrado com os filtros aplicados.
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground" data-cy="products-empty">
              Nenhum produto cadastrado. Clique em "Novo Produto" para começar.
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este produto? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmCloseProduct} onOpenChange={setConfirmCloseProduct}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fechar edição do produto?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja fechar o modal de produto? As alterações não salvas serão perdidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmCloseProduct(false)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmCloseProduct(false);
                setOpen(false);
              }}
            >
              Fechar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmCloseFormEditor} onOpenChange={setConfirmCloseFormEditor}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fechar edição do formulário?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja fechar o modal de formulários? As alterações não salvas serão perdidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmCloseFormEditor(false)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmCloseFormEditor(false);
                setIsQuickFormEditorOpen(false);
              }}
            >
              Fechar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {productProfile && (() => {
        const p = (products || []).find((x) => x.id === productProfile?.id);
        const infoItems = [
          (p as any)?.codigo_sku ? { value: `SKU: ${(p as any).codigo_sku}` } : null,
          (p as any)?.product_category_id ? { value: "Categoria vinculada", badge: true } : null,
        ].filter(Boolean) as any;
        const rightBadges = p ? [{ label: (p as any).status, className: "uppercase mr-2 bg-blue-600 text-white" }] : [];
        return (
          <RaioXModal
            open={!!productProfile}
            onOpenChange={() => setProductProfile(null)}
            imageUrl={p?.foto_url || null}
            entityTypeLabel="Produto"
            entityName={p?.nome || productProfile.name}
            infoItems={infoItems}
            rightBadges={rightBadges}
            showPeriodControls
            initialFrom={startOfDay(subDays(new Date(), 6))}
            initialTo={endOfDay(new Date())}
            onPeriodChange={(from, to) => { setRxFrom(from); setRxTo(to); }}
          renderContent={({ periodRange, setLoading }) => (
            productProfile ? (
              <ProductDetailsView id={productProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            ) : null
          )}
        />
        );
      })()}

      {productCategoryProfile && (
        <RaioXModal
          open={!!productCategoryProfile}
          onOpenChange={() => setProductCategoryProfile(null)}
          imageUrl={null}
          entityTypeLabel="Categoria de Produto"
          entityName={productCategoryProfile.name}
          infoItems={[]}
          showPeriodControls
          initialFrom={startOfDay(subDays(new Date(), 6))}
          initialTo={endOfDay(new Date())}
          onPeriodChange={(from, to) => { setRxFrom(from); setRxTo(to); }}
        renderContent={({ periodRange }) => (
          productCategoryProfile ? (
            <div />
          ) : null
        )}
      />
      )}
    </div>
  );
}
